import { nevisignFetch } from "@nevisign/reader-core";

/**
 * Mobile / reader flow (do not send private keys):
 * 1. generateDeviceCsr() or native Keystore/Secure Enclave → PKCS#10 PEM
 * 2. POST /certificates/csr with { csr } — identity comes from the verified e-sign profile
 * 3. poll GET /certificates/:id until status=issued
 * 4. GET /certificates/chain (optional)
 * 5. Web: persist cert PEM + CryptoKey in IndexedDB (never private key bytes)
 *    Native: install cert+chain in OS/Keystore
 * 6. POST /certificates/:id/revoke when needed
 *
 * Same JWT as the rest of the platform (Authorization: Bearer).
 */

export type CertificateIssuance = {
  id: string;
  status: "pending" | "issued" | "failed" | "revoked" | string;
  profileName: string;
  csrFingerprintSha256: string;
  csrSubject: string;
  serialNumber: string | null;
  uniqueId: string | null;
  notBefore: string | null;
  notAfter: string | null;
  certificatePem: string | null;
  chainPem: string | null;
  mode: string;
  azmaMessage: string | null;
  createdAt: string;
  updatedAt: string;
  revokedAt: string | null;
};

async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text.trim()) throw new Error("پاسخ خالی از سرور");
  let data: T & { error?: { message?: string; code?: string } };
  try {
    data = JSON.parse(text) as typeof data;
  } catch {
    throw new Error("پاسخ سرور قابل خواندن نبود");
  }
  if (!res.ok) {
    const msg =
      (typeof data.error?.message === "string" && data.error.message) ||
      `خطای صدور گواهی (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

export async function submitCertificateCsr(
  apiBase: string,
  input: {
    csr: string;
    profileName?: string;
  },
): Promise<CertificateIssuance> {
  if (/PRIVATE KEY/i.test(input.csr)) {
    throw new Error("کلید خصوصی نباید به سرور ارسال شود");
  }
  const res = await nevisignFetch(apiBase, "/certificates/csr", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export async function getCertificateIssuance(
  apiBase: string,
  id: string,
): Promise<CertificateIssuance> {
  const res = await nevisignFetch(apiBase, `/certificates/${encodeURIComponent(id)}`, {
    cache: "no-store",
  });
  return readJson(res);
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

/** Poll GET /certificates/:id until issued, failed, or timeout. Mock CA is usually immediate. */
export async function pollCertificateIssuance(
  apiBase: string,
  id: string,
  options?: {
    timeoutMs?: number;
    signal?: AbortSignal;
    onTick?: (current: CertificateIssuance) => void;
  },
): Promise<CertificateIssuance> {
  const timeoutMs = options?.timeoutMs ?? 60_000;
  const started = Date.now();
  let delayMs = 400;
  while (Date.now() - started < timeoutMs) {
    const current = await getCertificateIssuance(apiBase, id);
    options?.onTick?.(current);
    if (current.status === "issued" && current.certificatePem) return current;
    if (current.status === "failed" || current.status === "revoked") {
      throw new Error(current.azmaMessage || "صدور گواهی ناموفق بود");
    }
    await sleep(delayMs, options?.signal);
    delayMs = Math.min(Math.round(delayMs * 1.5), 2_000);
  }
  throw new Error("زمان صدور گواهی به پایان رسید. دوباره تلاش کنید.");
}

export async function getCertificateChain(
  apiBase: string,
): Promise<{ chainPem: string; source: string; mode: string }> {
  const res = await nevisignFetch(apiBase, "/certificates/chain", { cache: "no-store" });
  return readJson(res);
}

export async function revokeCertificate(
  apiBase: string,
  id: string,
  input: { reason?: number; description?: string; nationalId?: string } = {},
): Promise<CertificateIssuance> {
  const res = await nevisignFetch(apiBase, `/certificates/${encodeURIComponent(id)}/revoke`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}
